
import React from 'react';

import { translate } from '../../../base/i18n';
import { IconMicDisabled } from '../../../base/icons';
import { connect } from '../../../base/redux';
import { isRemoteAudioTrackMuted } from '../../../subtitles/functions.web';
import AbstractMuteButton, {
    type Props,
    _mapStateToProps
} from '../AbstractMuteButton';

import VideoMenuButton from './VideoMenuButton';

/**
 * Web-only override of {@code AbstractMuteButton}'s shared (also used by native) {@code _mapStateToProps}: recomputes
 * {@code _audioTrackMuted} with {@link isRemoteAudioTrackMuted} instead of the generic track lookup, since only web
 * currently subscribes to per-language synthetic translated-audio tracks (see subtitles/subscriber.js) - the
 * participantId collision that requires this only exists there.
 *
 * @param {Object} state - Redux state.
 * @param {Object} ownProps - Properties of component.
 * @private
 * @returns {{ _audioTrackMuted: boolean }}
 */
function _webMapStateToProps(state: Object, ownProps: Props) {
    return {
        ..._mapStateToProps(state, ownProps),
        _audioTrackMuted: isRemoteAudioTrackMuted(state['features/base/tracks'], ownProps.participantID)
    };
}

/**
 * Implements a React {@link Component} which displays a button for audio muting
 * a participant in the conference.
 *
 * NOTE: At the time of writing this is a button that doesn't use the
 * {@code AbstractButton} base component, but is inherited from the same
 * super class ({@code AbstractMuteButton} that extends {@code AbstractButton})
 * for the sake of code sharing between web and mobile. Once web uses the
 * {@code AbstractButton} base component, this can be fully removed.
 */
class MuteButton extends AbstractMuteButton {
    /**
     * Instantiates a new {@code Component}.
     *
     * @inheritdoc
     */
    constructor(props: Props) {
        super(props);

        this._onClick = this._handleClick.bind(this);
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { _audioTrackMuted, participantID, t } = this.props;
        const muteConfig = _audioTrackMuted ? {
            translationKey: 'videothumbnail.muted',
            muteClassName: 'mutelink disabled'
        } : {
            translationKey: 'videothumbnail.domute',
            muteClassName: 'mutelink'
        };

        return (
            <VideoMenuButton
                buttonText = { t(muteConfig.translationKey) }
                displayClass = { muteConfig.muteClassName }
                icon = { IconMicDisabled }
                id = { `mutelink_${participantID}` }

                onClick = { this._onClick } />
        );
    }

    _onClick: () => void;
}

export default translate(connect(_webMapStateToProps)(MuteButton));
